import React, { Fragment, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Text } from 'localmed-core';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/react-hooks';

import { getQueryFromLocation } from '../../../shared/utils/urls';

import {
  DiGenericLoadingPage,
  DiLayout,
  DiNotFoundPage,
  LmHeader,
} from '../../../shared/components/DiLayout';
import isForbidden from '../../../shared/utils/isForbidden';
import MarkSchedulingPageSeenMutation from './MarkSchedulingPageSeenMutation.graphql';

import { UniversalContext } from '../../../_universal';
import SchedulingPageBody from './components/SchedulingPageBody';
import SchedulingPageQuery from './SchedulingPageQuery';
import type { IScheduleRequestAttempt } from '../types';
import { DILM_CLIENT_CONTEXT } from '../../../shared/constants';
import { DiButton } from '../../../shared/components/DiComponents';
import { IRouteProps, IUniversalContext } from '../../../types';

export default function SchedulingPageV2(props: IRouteProps) {
  const { id } = props.match.params;
  const { token } = getQueryFromLocation(props.location);
  const { trackEvent }: IUniversalContext = useContext<any>(UniversalContext);

  const [markScheduleRequestAttemptAsSeen] = useMutation(MarkSchedulingPageSeenMutation, {
    variables: { scheduleRequestAttemptId: id, token },
    context: DILM_CLIENT_CONTEXT,
  });

  useEffect(() => {
    markScheduleRequestAttemptAsSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <SchedulingPageQuery id={id} token={token}>
      {({ scheduleRequestAttempt, loading, error, refetch }) => {
        if (loading) return <DiGenericLoadingPage />;

        if (isForbidden(error)) {
          return (
            <DiLayout>
              <>Forbidden</>
            </DiLayout>
          );
        }

        if (error) {
          return (
            <DiNotFoundPage>
              <Fragment>
                <Text>
                  <FormattedMessage
                    id="scheduling.SchedulingPage.PageNotFoundMessage"
                    defaultMessage="We’re sorry, something unexpected happened. We’ve been notified about this issue. Please try again in a few minutes."
                  />
                </Text>
                <DiButton onClick={() => refetch()} size="xl" mt="xl">
                  Retry
                </DiButton>
              </Fragment>
            </DiNotFoundPage>
          );
        }
        if (!scheduleRequestAttempt) {
          return (
            <DiNotFoundPage>
              <Fragment>
                <Text>
                  <FormattedMessage
                    id="scheduling.SchedulingPage.PageNotFoundMessage"
                    defaultMessage="Not Found."
                  />
                </Text>
                <Text>
                  <FormattedMessage
                    id="scheduling.SchedulingPage.PageNotFoundMessage"
                    defaultMessage="If this problem keeps happening, give us a call and we would be happy to help."
                  />
                </Text>
              </Fragment>
            </DiNotFoundPage>
          );
        }

        const { scheduleRequest }: IScheduleRequestAttempt = scheduleRequestAttempt;
        const { lmOffice, diPractice } = scheduleRequest.clinicMapping;

        const trackSchedulingEvent = (
          event,
          component = 'SchedulingPage',
          additionalProperties = {}
        ) => {
          trackEvent({
            event,
            properties: {
              workflow: 'SCHEDULE_REQUEST',
              component,
              scheduleRequestAttemptId: scheduleRequestAttempt.id,
              scheduleRequestId: scheduleRequest.id,
              clinicMappingId: scheduleRequest.clinicMapping.id,
              lmOfficeId: lmOffice.id,
              diPracticeId: diPractice.id,
              lmReasonForVisitId: scheduleRequest.lmReasonForVisit.id,
              topic: scheduleRequest.topic,
              ...additionalProperties,
            },
          });
        };

        return (
          <DiLayout
            header={<LmHeader office={lmOffice} practice={lmOffice.lmPractice} />}
            footerText="Powered by Localmed"
            size="lg"
          >
            <Helmet>
              <title>
                {`Schedule an Appointment | ${
                  lmOffice.messagingName || lmOffice.lmPractice.display
                }`}
              </title>
              <meta name="robots" content="noindex" />
            </Helmet>
            <SchedulingPageBody
              scheduleRequestAttempt={scheduleRequestAttempt}
              token={token as string}
              trackSchedulingEvent={trackSchedulingEvent}
            />
          </DiLayout>
        );
      }}
    </SchedulingPageQuery>
  );
}
